// pages/ExpensePurchaseOrder/EpoApprovalsInbox.js
//
// "EPO Approvals Inbox" — pending approvals assigned to the current user.
//
// R42 — modernized layout (DashboardKit page header + Card) and added
// column-filter row matching the rest of EPO (R41 pattern).

import React, { useEffect, useMemo, useState } from "react";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import { Container, Card, CardBody, Badge, Alert } from "reactstrap";
import useEpoReviewModal from "../../hooks/useEpoReviewModal";
import {
    DashboardPageHeader,
    EmptyState,
    TableSkeleton,
    ColumnFilterRow,
    applyColumnFilters,
} from "../../Components/Common/DashboardKit";
import {
    approveEpo,
    getApprovalsInbox,
    rejectEpo,
} from "../../services/epoApprovalsApi";

const fmtMoney = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    if (!isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};
const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleString(); } catch { return "—"; }
};

export default function EpoApprovalsInbox() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [activeRow, setActiveRow] = useState(null);
    const [comment, setComment] = useState("");
    const [mode, setMode] = useState("approve"); // approve | reject
    const [saving, setSaving] = useState(false);
    const epoReview = useEpoReviewModal({ permissions: { canApprove: true }, onActionDone: () => load() });

    // R42 — per-column filters.
    const [columnFilters, setColumnFilters] = useState({});

    async function load() {
        setLoading(true);
        setErr(null);
        try {
            const data = await getApprovalsInbox();
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setErr(e?.response?.data || e.message || "Failed to load inbox");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    const totalPending = useMemo(() => rows.length, [rows]);

    function openApprove(row) {
        setActiveRow(row);
        setMode("approve");
        setComment("");
        setModalOpen(true);
    }
    function openReject(row) {
        setActiveRow(row);
        setMode("reject");
        setComment("");
        setModalOpen(true);
    }

    async function submitDecision() {
        if (!activeRow) return;
        if (mode === "reject" && !comment.trim()) {
            alert("Reject requires a comment.");
            return;
        }
        setSaving(true);
        try {
            const epoNum = activeRow.epoNum;
            if (mode === "approve") await approveEpo(epoNum, comment);
            else await rejectEpo(epoNum, comment);
            setModalOpen(false);
            setActiveRow(null);
            await load();
        } catch (e) {
            alert(e?.response?.data || e.message || "Action failed");
        } finally {
            setSaving(false);
        }
    }

    // R42 — column definitions.
    const columns = useMemo(() => [
        { id: "epoNum",    label: "EPO #",     type: "text",
          get: r => r.epoNum },
        { id: "empId",     label: "Requester", type: "text",
          get: r => r.empId },
        { id: "amount",    label: "Amount",    type: "text",
          get: r => r.amount != null ? String(r.amount) : "" },
        { id: "stamp",     label: "Submitted", type: "text",
          get: r => fmtDate(r.stamp) },
        { id: "actions",   label: "Actions",   type: "none" },
    ], []);

    const visibleRowsBase = useMemo(
        () => applyColumnFilters(rows, columns, columnFilters),
        [rows, columns, columnFilters]
    );

    // Round 30 — click-to-sort (type-aware) on the loaded rows.
    const { sorted: visibleRows, sort, toggleSort } = useTableSort(visibleRowsBase, columns);
    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title="EPO Approvals Inbox"
                    breadcrumb={[{ label: "EPO", to: "/workflows?wf=epo" }, { label: "EPO Approvals Inbox" }]}
                    subtitle={`${totalPending} EPO(s) pending your approval`}
                    breadcrumbItems={[{ label: "EPO", href: "/epo" }, { label: "Approvals Inbox" }]}
                />

                {err && <Alert color="danger" className="mt-2" toggle={() => setErr(null)}>{String(err)}</Alert>}

                <Card className="mt-3">
                    <CardBody>
                        {loading ? (
                            <div className="table-responsive epo-sticky-head">
                                    <table className="table table-striped table-bordered mb-0 align-middle">
                                        <thead className="table-light">
                                            <tr>{columns.map((c) => <th key={c.id}>{c.label}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            <TableSkeleton rows={6} columns={columns.length} />
                                        </tbody>
                                    </table>
                                </div>
                        ) : rows.length === 0 ? (
                            <EmptyState
                                title="Inbox empty"
                                description="No pending approvals are assigned to you."
                            />
                        ) : (
                            <>
                                                                {/* Round 29 — CSV of the full list (client-side data, respects filters) */}
                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-soft-primary btn-sm" onClick={() => exportRowsToCsv("epos-awaiting-my-approval.csv", columns.filter((c) => c.type !== "none"), rows)}>
                                        <i className="ri-download-2-line align-bottom me-1" />Export CSV ({(rows).length})
                                    </button>
                                </div>
<div className="table-responsive epo-sticky-head">
                                    <table className="table table-striped table-bordered mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                {columns.map((c) => <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />)}
                                            </tr>
                                            <ColumnFilterRow
                                                columns={columns}
                                                filters={columnFilters}
                                                onChange={setColumnFilters}
                                            />
                                        </thead>
                                        <tbody>
                                            {visibleRows.map((r) => (
                                                <tr key={r.id} onDoubleClick={() => epoReview.open(r.epoNum)} style={{ cursor: "pointer" }}>
                                                    <td><strong>{r.epoNum}</strong></td>
                                                    <td>{r.empId}</td>
                                                    <td>{fmtMoney(r.amount)}</td>
                                                    <td>{fmtDate(r.stamp)}</td>
                                                    <td className="text-nowrap">
                                                        <button
                                                            className="btn btn-link btn-sm p-0 me-2"
                                                            onClick={() => epoReview.open(r.epoNum)}
                                                        >
                                                            View
                                                        </button>
                                                        <button
                                                            className="btn btn-success btn-sm me-1"
                                                            onClick={() => openApprove(r)}
                                                        >
                                                            Approve
                                                        </button>
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => openReject(r)}
                                                        >
                                                            Reject
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {visibleRows.length === 0 && (
                                    <div className="text-muted text-center py-3 small">
                                        No rows match the current filters.
                                    </div>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* Simple modal (Bootstrap-ish) — unchanged from R41. */}
                {modalOpen && (
                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0,0,0,0.35)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 9999,
                        }}
                    >
                        <div
                            style={{
                                background: "#fff",
                                padding: 20,
                                width: 520,
                                borderRadius: 6,
                                boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                            }}
                        >
                            <h4 style={{ marginTop: 0 }}>
                                {mode === "approve" ? "Approve" : "Reject"} EPO {activeRow?.epoNum}
                            </h4>

                            <div style={{ marginBottom: 8 }}>
                                Requester: <b>{activeRow?.empId}</b> | Amount: <b>{fmtMoney(activeRow?.amount)}</b>
                            </div>

                            <div style={{ marginBottom: 10 }}>
                                <label>Comment {mode === "reject" ? "(required)" : "(optional)"}</label>
                                <textarea
                                    className="form-control"
                                    rows="4"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                />
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                                <button
                                    className="btn btn-default"
                                    onClick={() => setModalOpen(false)}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={mode === "approve" ? "btn btn-success" : "btn btn-danger"}
                                    onClick={submitDecision}
                                    disabled={saving}
                                >
                                    {saving ? "Saving…" : mode === "approve" ? "Approve" : "Reject"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </Container>
        {epoReview.element}
        </div>
    );
}
